import {
  Box,
  Text,
  Flex,
  Checkbox,
  useToast,
  Input,
  IconButton,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import _, { isEmpty, omit } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { generateExtendedResponseTapeFieldNames } from '@finley-technologies/calc-agent';

import { WipConfig } from '../../types';
import {
  AddIconButton,
  DeleteIconButton,
  SaveIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Select } from 'packages/common-frontend/components/Select';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';

export const assignedLoanTapeCsvConfigTypeOptions: {
  value: CreditAgreement.Config.LoanProductMechanics.AssignedLoanTapeCsvConfigType;
  label: string;
}[] = [
  { value: 'exclude' as const, label: 'exclude' },
  { value: 'include' as const, label: 'include' },
];

type AssignedLoanTapeCsvConfigProps = {
  wipConfig?: WipConfig;
  basePropertyKey: string;
  csvType: keyof CreditAgreement.Config.LoanProductMechanics.AssignedLoanTapeCsvConfigs;
  assignedLoanTapeCsvConfig:
    | CreditAgreement.Config.LoanProductMechanics.AssignedLoanTapeCsvConfig
    | undefined;
  // raw tapes have no known safe fields, so this will free text
  knownFields?: string[];
};

const AssignedLoanTapeCsvConfig = ({
  wipConfig,
  basePropertyKey,
  csvType,
  assignedLoanTapeCsvConfig,
  knownFields,
}: AssignedLoanTapeCsvConfigProps) => {
  const propertyKey = `${basePropertyKey}.${csvType}`;

  const toast = useToast();

  const [show, setShow] = useState(false);

  const handleToggle = () => setShow(!show);

  const handleAdd = (wc: WipConfig) => {
    const emptyConfig: CreditAgreement.Config.LoanProductMechanics.AssignedLoanTapeCsvConfig = {
      type: 'exclude',
      fields: [],
    };
    wc.formProps.setFieldValue(propertyKey, emptyConfig);
  };

  const handleDelete = (wc: WipConfig) => {
    const props = getIn(wc.formProps.values, basePropertyKey);
    wc.formProps.setFieldValue(basePropertyKey, omit(props, csvType));
  };

  const fieldSet = useMemo(() => {
    const set = new Set<string>(
      assignedLoanTapeCsvConfig
        ? assignedLoanTapeCsvConfig.fields.map((field) => field)
        : [],
    );
    return set;
  }, [assignedLoanTapeCsvConfig]);

  const handleSelectionChange = ({
    wc,
    field,
  }: {
    wc: WipConfig;
    field: string;
  }) => {
    const key = `${propertyKey}.fields`;

    const fields = getIn(wc.formProps.values, key);

    if (fieldSet.has(field)) {
      if (Array.isArray(fields)) {
        const updatedFields = fields.filter(
          (existingField) => existingField !== field,
        );
        wc.formProps.setFieldValue(key, updatedFields);
      } else {
        toast({
          status: 'error',
          position: 'top',
          isClosable: true,
          title: `Expected fields to be an array, but got ${typeof fields}`,
        });
      }
    } else {
      wc.formProps.setFieldValue(key, [...fields, field]);
    }
  };

  const handleRawFieldChange = ({
    wc,
    field,
    idx,
  }: {
    wc: WipConfig;
    field: string;
    idx: number;
  }) => {
    const key = `${propertyKey}.fields[${idx}]`;
    wc.formProps.setFieldValue(key, field);
  };

  const handleRawFieldAdd = (wc: WipConfig) => {
    const key = `${propertyKey}.fields`;
    const fields = getIn(wc.formProps.values, key);
    if (Array.isArray(fields)) {
      wc.formProps.setFieldValue(key, [...fields, 'placeHolder']);
    } else {
      toast({
        status: 'error',
        position: 'top',
        isClosable: true,
        title: `Expected fields to be an array, but got ${typeof fields}`,
      });
    }
  };

  const handleRawFieldDelete = ({
    wc,
    idx,
  }: {
    wc: WipConfig;
    idx: number;
  }) => {
    const key = `${propertyKey}.fields`;
    const fields = getIn(wc.formProps.values, key);
    if (Array.isArray(fields)) {
      const updatedFields = fields.filter((__, index) => index !== idx);
      wc.formProps.setFieldValue(key, updatedFields);
    } else {
      toast({
        status: 'error',
        position: 'top',
        isClosable: true,
        title: `Expected fields to be an array, but got ${typeof fields}`,
      });
    }
  };

  const handleTypeChange = ({
    wc,
    assignedCsvConfigType,
  }: {
    wc: WipConfig;
    assignedCsvConfigType: CreditAgreement.Config.LoanProductMechanics.AssignedLoanTapeCsvConfigType;
  }) => {
    const key = `${propertyKey}.type`;
    wc.formProps.setFieldValue(key, assignedCsvConfigType);
  };

  useEffect(() => {
    if (wipConfig && knownFields && assignedLoanTapeCsvConfig) {
      const knownFieldSet = new Set(knownFields);
      const [
        validFields,
        invalidFields,
      ] = _.partition(assignedLoanTapeCsvConfig.fields, (field) =>
        knownFieldSet.has(field),
      );

      if (invalidFields.length) {
        toast({
          title: `Removed [invalidFields:${invalidFields}] from ${csvType} loan tape csv config.`,
          position: 'top',
          status: 'warning',
          isClosable: true,
        });

        const key = `${propertyKey}.fields`;
        wipConfig.formProps.setFieldValue(key, validFields);
      }
    }
  }, [
    assignedLoanTapeCsvConfig,
    propertyKey,
    csvType,
    knownFields,
    toast,
    wipConfig,
  ]);

  // if not wip config and no config, do not render
  if (!wipConfig && !assignedLoanTapeCsvConfig) {
    return null;
  }

  return (
    <Box>
      <Flex alignItems="center">
        {!assignedLoanTapeCsvConfig &&
          wipConfig?.isMutateAllowed(propertyKey) && (
            <AddIconButton
              label="Add"
              ariaLabel="Add"
              onClick={() => handleAdd(wipConfig)}
            />
          )}
        {assignedLoanTapeCsvConfig &&
          wipConfig?.isMutateAllowed(propertyKey) && (
            <DeleteIconButton
              label="Delete"
              ariaLabel="Add"
              onClick={() => handleDelete(wipConfig)}
            />
          )}
        <Text mr="3">{csvType} csv configuration</Text>
        {assignedLoanTapeCsvConfig && !show && (
          <IconButton
            icon={<MdExpandMore />}
            onClick={handleToggle}
            aria-label="Expand"
            size="xs"
          />
        )}
        {assignedLoanTapeCsvConfig && show && (
          <IconButton
            icon={<MdExpandLess />}
            onClick={handleToggle}
            aria-label="Expand"
            size="xs"
          />
        )}
      </Flex>
      {assignedLoanTapeCsvConfig && show && (
        <Box pl="10">
          <Flex alignItems="center" width="sm">
            <Text mr="3">Type:</Text>
            <Select
              value={assignedLoanTapeCsvConfig.type}
              isDisabled={!wipConfig?.isMutateAllowed(propertyKey)}
              onChange={(val) => {
                if (val && wipConfig) {
                  handleTypeChange({
                    wc: wipConfig,
                    assignedCsvConfigType: val,
                  });
                }
              }}
              options={assignedLoanTapeCsvConfigTypeOptions}
            />
          </Flex>
          <Flex alignItems="center">
            <Text>Fields:</Text>
            {!knownFields && wipConfig?.isMutateAllowed(propertyKey) && (
              <AddIconButton
                label="Add field"
                ariaLabel="Add field"
                onClick={() => handleRawFieldAdd(wipConfig)}
              />
            )}
          </Flex>
          {knownFields
            ? knownFields.map((field) => {
                return (
                  <Flex key={field}>
                    <Checkbox
                      mr="3"
                      isChecked={fieldSet.has(field)}
                      isDisabled={!wipConfig?.isMutateAllowed(propertyKey)}
                      onChange={() => {
                        if (wipConfig?.isMutateAllowed(propertyKey)) {
                          handleSelectionChange({ wc: wipConfig, field });
                        }
                      }}
                    />
                    <Text>{field}</Text>
                  </Flex>
                );
              })
            : assignedLoanTapeCsvConfig.fields.map((field, idx) => {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <Flex key={`field-${idx}`} alignItems="center">
                    <Input
                      type="text"
                      width="xl"
                      isDisabled={!wipConfig}
                      value={field}
                      onChange={(e) => {
                        if (wipConfig?.isMutateAllowed(propertyKey)) {
                          handleRawFieldChange({
                            wc: wipConfig,
                            field: e.target.value,
                            idx,
                          });
                        }
                      }}
                    />
                    {wipConfig?.isMutateAllowed(propertyKey) && (
                      <DeleteIconButton
                        label="delete"
                        ariaLabel="delete"
                        onClick={() =>
                          handleRawFieldDelete({ wc: wipConfig, idx })
                        }
                      />
                    )}
                  </Flex>
                );
              })}
        </Box>
      )}
    </Box>
  );
};

type AssignedLoanTapeCsvConfigsProps = {
  wipConfig: WipConfig | undefined;
  basePropertyKey: string;
  idx: number;
  baseFields: string[];
  intermediateFields: string[];
  eligibilityPropertyKeys: string[];
  allConcentrationLimitKeys: string[];
  assignedLoanTapeCsvConfigs: CreditAgreement.Config.LoanProductMechanics.AssignedLoanTapeCsvConfigs;
};
export const AssignedLoanTapeCsvConfigs = ({
  wipConfig,
  basePropertyKey,
  idx,
  assignedLoanTapeCsvConfigs,
  baseFields,
  intermediateFields,
  eligibilityPropertyKeys,
  allConcentrationLimitKeys,
}: AssignedLoanTapeCsvConfigsProps) => {
  const propertyKey = `${basePropertyKey}[${idx}]._config_.assignedLoanTapeCsvConfigs`;

  // if empty and not in wip mode, nothing to render
  if (isEmpty(assignedLoanTapeCsvConfigs) && !wipConfig) {
    return null;
  }

  return (
    <Box mb="35px" width="100%">
      <Flex alignItems="center" mt="2">
        <Text fontWeight="bold" fontSize="18px" mr="3">
          Assigned Loan Tape Csv Configurations
        </Text>
        {wipConfig?.isMutateAllowed(propertyKey) && (
          <SaveIconButton
            ariaLabel="save assigned loan tape csv configurations"
            label="Save Assigned Loan Tape CSV Configurations"
            onClick={() => {
              wipConfig.saveCreditAgreementFullArtifactWip({
                variables: {
                  creditAgreementFullArtifactWip: {
                    id: wipConfig.formProps.values.id,
                    creditFacilityId:
                      wipConfig.formProps.values.creditFacilityId,
                    creditAgreement: JSON.stringify(wipConfig.formProps.values),
                  },
                },
              });
            }}
          />
        )}
      </Flex>
      {wipConfig?.isMutateAllowed(propertyKey) && (
        <Text size="xs" fontStyle="italic" mb="3">
          Invalid column names and empty configurations will be ignored.
        </Text>
      )}

      <AssignedLoanTapeCsvConfig
        basePropertyKey={propertyKey}
        csvType="rawAssigned"
        wipConfig={wipConfig}
        assignedLoanTapeCsvConfig={assignedLoanTapeCsvConfigs.rawAssigned}
        // knownFields are unknown for rawAssigned
        // runtime behavior is quite forgiving (i.e. non existing configured columns will be ignored)
        // but cap markets will need to QA there way to success
      />
      <AssignedLoanTapeCsvConfig
        basePropertyKey={propertyKey}
        csvType="generatedAssigned"
        wipConfig={wipConfig}
        assignedLoanTapeCsvConfig={assignedLoanTapeCsvConfigs.generatedAssigned}
        knownFields={baseFields} // generatedAssigned uses simple baseFields
      />
      <AssignedLoanTapeCsvConfig
        basePropertyKey={propertyKey}
        csvType="extendedResponseTape"
        wipConfig={wipConfig}
        assignedLoanTapeCsvConfig={
          assignedLoanTapeCsvConfigs.extendedResponseTape
        }
        knownFields={generateExtendedResponseTapeFieldNames({
          baseFields,
          intermediateFields,
          eligibilityPropertyKeys,
          allConcentrationLimitKeys,
        })}
      />
    </Box>
  );
};
